import type { ReactNode } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Check,
  MonitorSmartphone,
  QrCode,
  ShieldCheck,
  Undo2,
} from 'lucide-react';
import { BotaoLink } from '../../componentes/ui';
import { reais } from '../../util/formato';
import { BotaoAncora } from './BotaoAncora';
import { JanelaDownloads } from './Mockups';
import base from './publico.module.css';
import css from './Chamada.module.css';

/** Primeira dobra: o que é, para quem, quanto custa começar — e o produto. */
export function Chamada({
  logado,
  bonusCentavos,
}: {
  logado: boolean;
  /** 0 enquanto carrega ou se a promoção estiver desligada. */
  bonusCentavos: number;
}) {
  return (
    <section className={css.chamada}>
      <div className={`${base.conteudo} ${css.grade}`}>
        <div className={css.texto}>
          <span className={css.selo}>Para escritórios contábeis e fiscais</span>
          <h1 className={css.titulo}>
            Os XMLs do seu cliente,{' '}
            <span className={css.tituloDestaque}>sem caçar nota a nota.</span>
          </h1>
          <p className={css.sub}>
            Envie o SPED e nós extraímos as chaves; ou traga a lista pronta —
            uma, mil ou um milhão. Buscamos os arquivos e devolvemos tudo num
            ZIP, com o XML que a apuração, a auditoria e a guarda legal exigem
            e o PDF não substitui.
          </p>

          <ul className={css.marcadores}>
            <Marcador>
              <b>Sem certificado digital.</b> Não pedimos o A1 do seu cliente,
              nem procuração eletrônica. A chave de acesso basta.
            </Marcador>
            <Marcador>
              <b>Sem instalar nada.</b> Roda no navegador; o escritório
              inteiro usa a mesma conta.
            </Marcador>
            {/* Some sozinho quando a promoção acaba: o valor vem de
                `GET /creditos/precos`, não de um texto fixo. */}
            {bonusCentavos > 0 && (
              <Marcador>
                <b className={css.bonus}>
                  {reais(bonusCentavos)} em créditos para testar.
                </b>{' '}
                Entram na conta assim que você confirmar o e-mail.
              </Marcador>
            )}
          </ul>

          <div className={css.acoes}>
            <BotaoLink
              to={logado ? '/painel/nova-busca' : '/criar-conta'}
              data-clique="criar-conta-chamada"
              aparencia="destaque"
              grande
              iconeDireita={<ArrowRight />}
            >
              {logado ? 'Nova busca' : 'Criar conta grátis'}
            </BotaoLink>
            <BotaoAncora href="#precos" data-clique="ver-precos-chamada" grande>
              Ver preços
            </BotaoAncora>
          </div>
          <span className={css.nota}>
            Sem mensalidade e sem fidelidade. Você paga por XML entregue.
          </span>
        </div>

        <div className={css.visual}>
          <JanelaDownloads />
        </div>
      </div>

      <div className={base.conteudo}>
        <ul className={css.faixa} aria-label="Por que confiar">
          <ItemFaixa icone={<BadgeCheck />}>
            XML original, com a assinatura digital do emitente
          </ItemFaixa>
          <ItemFaixa icone={<Undo2 />}>
            Chave não encontrada não é cobrada
          </ItemFaixa>
          <ItemFaixa icone={<QrCode />}>Recarga por Pix, via Asaas</ItemFaixa>
          <ItemFaixa icone={<ShieldCheck />}>
            LGPD, sem rastreadores de terceiros
          </ItemFaixa>
          <ItemFaixa icone={<MonitorSmartphone />}>
            Nada para instalar
          </ItemFaixa>
        </ul>
      </div>
    </section>
  );
}

function Marcador({ children }: { children: ReactNode }) {
  return (
    <li className={css.marcador}>
      <span className={css.tique} aria-hidden="true">
        <Check />
      </span>
      <span>{children}</span>
    </li>
  );
}

function ItemFaixa({ icone, children }: { icone: ReactNode; children: ReactNode }) {
  return (
    <li className={css.itemFaixa}>
      <span className={css.iconeFaixa} aria-hidden="true">
        {icone}
      </span>
      {children}
    </li>
  );
}
